import { useEffect, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { BaseFeedbackConIcona } from 'feedback'
import { BaseButtons, BaseTextInput, BaseSelect, BaseDateInput } from 'inputs'
import { BaseGridLayout } from 'layout'
import { oggiInizioGiornata } from 'utils'
import ElenchiManager, { ELENCHI } from '../../form/ElenchiManager'
import { ENTI_VISURA_ANAGRAFICA, TIPI_VISURA } from '../../form/utilsElenchi'
import { OPERAZIONI_BASE } from '../../network/OperazioniBase'
import VisuraGenerica from '../VisuraGenerica'

const NOMI = {
  TIPO_VISURA: 'tipo_visura',
  ENTE: 'ente',
  CODICE_FISCALE_O_PATENTE: 'codiceFiscaleOPatente',
  CAMPO_DA_USARE: 'campoDaUsare',
  DATA_RIFERIMENTO: 'data_riferimento',
  MOTIVAZIONE: 'motivazione',
}

const CAMPI_DA_USARE = {
  PATENTE: 'patente',
  CODICE_FISCALE: 'codiceFiscale',
}

const LABEL_CAMPI_DA_USARE = {
  [CAMPI_DA_USARE.PATENTE]: 'Patente',
  [CAMPI_DA_USARE.CODICE_FISCALE]: 'Codice fiscale',
}

VisuraPersona.CAMPI_DA_USARE = CAMPI_DA_USARE

export default function VisuraPersona(props) {
  const {
    codiceFiscaleOPatente,
    campoDaUsare,
    importaPersona,
    chiaveTipoVisuraDefault = TIPI_VISURA.DATI_PATENTE,
    readOnly,
  } = props

  return (
    <div style={{ minWidth: 50 }}>
      <ElenchiManager
        elenchi={[ELENCHI.VISURE_CONFIG]}
        render={el => {
          const tipiVisura = el(ELENCHI.VISURE_CONFIG).anagrafica
          const nessunaVisuraDisponibile = tipiVisura.length === 0
          const tipoVisuraDefault = tipiVisura.find(({ value }) => value === chiaveTipoVisuraDefault) || tipiVisura[0]
          return (
            <VisuraGenerica
              titolo='Visura anagrafica'
              nessunaVisuraDisponibile={nessunaVisuraDisponibile}
              defaultValues={!nessunaVisuraDisponibile && {
                [NOMI.CODICE_FISCALE_O_PATENTE]: codiceFiscaleOPatente || '',
                [NOMI.CAMPO_DA_USARE]: campoDaUsare,
                [NOMI.TIPO_VISURA]: tipoVisuraDefault.value,
                [NOMI.ENTE]: tipoVisuraDefault.enti[0].value,
                [NOMI.MOTIVAZIONE]: '',
                [NOMI.DATA_RIFERIMENTO]: oggiInizioGiornata(),
              }}
              ContenutoFormVisura={() =>
                <FormVisuraPersona
                  tipiVisura={tipiVisura}
                  campoDaUsare={campoDaUsare}
                />
              }
              useLogicaVisura={useLogicaVisuraPersona}
              importaDati={importaPersona}
              {...(readOnly && { readOnly })}
            />
          )
        }}
      />
    </div>
  )
}



function FormVisuraPersona(props) {
  const { tipiVisura, campoDaUsare } = props

  const { watch, setValue, inAttesa } = useFormContext()
  const [tipoVisuraSelezionato, ente] = watch([NOMI.TIPO_VISURA, NOMI.ENTE])

  const enti = useMemo(() => {
    return tipiVisura.find(({ value }) => value === tipoVisuraSelezionato).enti
      .filter(({ value }) => {
        if (campoDaUsare === CAMPI_DA_USARE.PATENTE && value === ENTI_VISURA_ANAGRAFICA.ANPR) return false
        return true
      })
  }, [tipiVisura, tipoVisuraSelezionato, campoDaUsare])

  useEffect(() => {
    setValue(NOMI.ENTE, enti[0].value)
  }, [tipoVisuraSelezionato, enti])

  const propsCampiAnpr = {
    validazione: {
      validate: {
        required: valore => {
          if (ente !== ENTI_VISURA_ANAGRAFICA.ANPR) return true
          return Boolean(valore)
        }
      }
    },
    messaggiErrore: { required: 'Campo obbligatorio' },
    hidden: ente !== ENTI_VISURA_ANAGRAFICA.ANPR
  }

  return (
    <BaseGridLayout alignCenter justifyCenter disposizioneRighe={[4, 1, 1, 1, 1]}>
      <BaseTextInput
        label={LABEL_CAMPI_DA_USARE[campoDaUsare]}
        name={NOMI.CODICE_FISCALE_O_PATENTE}
        required
        autoFocus
      />
      <BaseTextInput
        name={NOMI.CAMPO_DA_USARE}
        hidden
      />
      <BaseSelect
        label='Tipo visura' name={NOMI.TIPO_VISURA}
        options={tipiVisura} hidden={tipiVisura.length === 1}
      />
      <BaseSelect
        label='Ente' name={NOMI.ENTE}
        options={enti} hidden={enti.length === 1}
      />

      <BaseTextInput
        label='Motivazione *' name={NOMI.MOTIVAZIONE}
        larghezza={33} multiline rows={2}
        {...propsCampiAnpr}
      />

      <BaseDateInput
        label='Data di riferimento *' name={NOMI.DATA_RIFERIMENTO}
        {...propsCampiAnpr}
      />

      <BaseButtons.Cerca
        testo='Visura'
        color='info'
        inAttesa={false}
        disabled={inAttesa}
        sx={{ mb: 3, mt: ente !== ENTI_VISURA_ANAGRAFICA.ANPR ? 6 : 2 }}
      />

      <BaseFeedbackConIcona.Attenzione
        messaggio='Attenzione: potrebbe comportare dei costi'
        hidden={ente !== ENTI_VISURA_ANAGRAFICA.MCTC}
      />
    </BaseGridLayout>
  )
}



function useLogicaVisuraPersona(props) {
  const { inviaOperazione, setRispostaVisura } = props

  async function eseguiVisura(valoriForm) {
    const {
      codiceFiscaleOPatente,
      campoDaUsare,
      ente,
      data_riferimento,
      motivazione,
    } = valoriForm

    const { ok, data } = await inviaOperazione(OPERAZIONI_BASE.VISURA_ANAGRAFICA, {
      [campoDaUsare]: codiceFiscaleOPatente,
      ente,
      ...(ente === ENTI_VISURA_ANAGRAFICA.ANPR && {
        data_riferimento,
        motivazione,
      })
    })

    if (ok) {
      const { persona, html, avvisi } = data
      setRispostaVisura({ risultato: persona, html, avvisi })
    }
  }

  return { eseguiVisura }
}